export const UI_SETTING_KEYS = {
  routingProfile: 'routing.profile',
  workspaceSearchPreferences: 'workspaceSearch.preferences',
  workspaceSearchLockListToMap: 'workspaceSearch.lockListToMap',
  libraryAllActivitiesLockListToMap: 'library.allActivities.lockListToMap',
  workspaceSearchSelectedWorkspaceIds: 'workspaceSearch.selectedWorkspaceIds',
  workspaceSearchVisibilityStates: 'workspaceSearch.visibilityStates',
  mapUiState: 'map-ui-state',
  mapCollectionsOverviewBounds: 'map.collectionsOverviewBounds',
  mapAdditionalTrackWarningAcknowledged: 'map.additionalTrackWarningAcknowledged',
  mapLayerPreferences: 'map.layerPreferences',
  mapStatsExpanded: 'map.statsExpanded',
  mapTourSort: 'map.tourSort',
  libraryPanelHeight: 'library.panelHeight',
  libraryAllActivitiesSort: 'library.allActivitiesSort',
  librarySelectedTourIds: 'library.selectedTourIds',
  libraryUploadedBatchIds: 'library.uploadedBatchIds',
  libraryShowOnlyUploadedBatch: 'library.showOnlyUploadedBatch',
  photoSlideshowAutoShowOverlay: 'photoSlideshow.autoShowOverlay',
  recordingAutoPauseEnabled: 'recording.autoPauseEnabled',
  offlineIncludePhotos: 'offline.includePhotos',
  offlineIncludeTerrain: 'offline.includeTerrain',
  connectivityForceOfflineMode: 'connectivity.forceOfflineMode',
  syncLastEventSyncTimestamp: 'sync.lastEventSyncTimestamp',
} as const

export type UiSettingChangeSource = 'local' | 'remote' | 'migration' | 'remove'

export type UiSettingDefinition = {
  key: string
  syncable: boolean
  legacyKeys?: string[]
  legacyLocalStorageKeys?: string[]
}

export type DynamicUiSettingDefinition = {
  prefix: string
  syncable: boolean
  legacyPrefix?: string
  toLegacyKey?: (suffix: string) => string
}

export const UI_SETTING_DEFINITIONS: UiSettingDefinition[] = [
  {
    key: UI_SETTING_KEYS.routingProfile,
    syncable: true,
    legacyKeys: ['routing-profile'],
  },
  {
    key: UI_SETTING_KEYS.workspaceSearchPreferences,
    syncable: true,
    legacyKeys: ['workspace-search-preferences'],
  },
  {
    key: UI_SETTING_KEYS.workspaceSearchLockListToMap,
    syncable: true,
    legacyKeys: ['workspace-search-panel-lock-to-map', 'mobile-workspace-search-panel-lock-to-map'],
  },
  {
    key: UI_SETTING_KEYS.libraryAllActivitiesLockListToMap,
    syncable: true,
    legacyKeys: ['all-activities-lock-to-map'],
  },
  {
    key: UI_SETTING_KEYS.workspaceSearchSelectedWorkspaceIds,
    syncable: false,
    legacyKeys: ['selected-search-workspace-ids'],
  },
  {
    key: UI_SETTING_KEYS.workspaceSearchVisibilityStates,
    syncable: false,
    legacyKeys: ['workspace-visibility-states'],
  },
  {
    key: UI_SETTING_KEYS.mapUiState,
    syncable: false,
    legacyKeys: ['ui-state'],
  },
  {
    key: UI_SETTING_KEYS.mapCollectionsOverviewBounds,
    syncable: false,
    legacyKeys: ['collections-overview-bounds'],
  },
  {
    key: UI_SETTING_KEYS.mapAdditionalTrackWarningAcknowledged,
    syncable: true,
    legacyKeys: ['additional-track-warning-acknowledged'],
  },
  {
    key: UI_SETTING_KEYS.mapLayerPreferences,
    syncable: true,
    legacyKeys: ['map-layer-preferences'],
  },
  {
    key: UI_SETTING_KEYS.mapStatsExpanded,
    syncable: true,
  },
  {
    key: UI_SETTING_KEYS.mapTourSort,
    syncable: true,
  },
  {
    key: UI_SETTING_KEYS.libraryPanelHeight,
    syncable: true,
    legacyKeys: ['library-panel-height'],
  },
  {
    key: UI_SETTING_KEYS.libraryAllActivitiesSort,
    syncable: true,
    legacyKeys: ['all-activities-sort'],
  },
  {
    key: UI_SETTING_KEYS.librarySelectedTourIds,
    syncable: false,
    legacyKeys: ['selected-library-tour-ids'],
  },
  {
    key: UI_SETTING_KEYS.libraryUploadedBatchIds,
    syncable: false,
    legacyKeys: ['uploaded-batch-ids'],
  },
  {
    key: UI_SETTING_KEYS.libraryShowOnlyUploadedBatch,
    syncable: false,
    legacyKeys: ['show-only-uploaded-batch'],
  },
  {
    key: UI_SETTING_KEYS.photoSlideshowAutoShowOverlay,
    syncable: false,
    legacyLocalStorageKeys: ['photoSlideshow.autoShowOverlay'],
  },
  {
    key: UI_SETTING_KEYS.recordingAutoPauseEnabled,
    syncable: true,
    legacyKeys: ['recording.autoPauseEnabled'],
  },
  {
    key: UI_SETTING_KEYS.offlineIncludePhotos,
    syncable: false,
    legacyKeys: ['offline.includePhotos'],
  },
  {
    key: UI_SETTING_KEYS.offlineIncludeTerrain,
    syncable: false,
  },
  {
    key: UI_SETTING_KEYS.connectivityForceOfflineMode,
    syncable: false,
    legacyKeys: ['force-offline-mode'],
  },
  {
    key: UI_SETTING_KEYS.syncLastEventSyncTimestamp,
    syncable: false,
    legacyKeys: ['last-sync-timestamp'],
  },
]

export const DYNAMIC_UI_SETTING_DEFINITIONS: DynamicUiSettingDefinition[] = []

const definitionsByKey = new Map(
  UI_SETTING_DEFINITIONS.map((definition) => [definition.key, definition]),
)

export function getUiSettingDefinition(key: string) {
  const exactDefinition = definitionsByKey.get(key)
  if (exactDefinition) return exactDefinition

  return DYNAMIC_UI_SETTING_DEFINITIONS.find((definition) => key.startsWith(definition.prefix))
}

export function isUiSettingSyncable(key: string): boolean {
  return getUiSettingDefinition(key)?.syncable === true
}
